import React from "react";
import Loader from "../Loader";
import {
    Avatar,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    CardHeader,
    Collapse,
    IconButton,
    List,
    ListItem,
    Typography
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {ArrowForwardIos} from "@material-ui/icons";
import {useTranslation} from "react-i18next";
import {useNavigate, useLocation, useParams} from 'react-router-dom';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {COMMUNICATOR_PAGE_PATH} from "../../../layouts/navigation/Navigator";
import {getAllComments} from "../../../api/CommentEndpoint";
import {RR_STATUS} from "../../../constants/RRConstants";
import {useSnackbar} from "notistack";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexGrow: 1
    },
    content: {
        display: 'flex',
        justifyContent: "space-between"
    },
    details: {
        display: "flex",
        flexDirection: "column"
    },
    expandIconOpen: {
        transform: 'rotate(180deg)',
    },
    caption: {
        fontWeight: "bold",
        marginRight: 5,
    },
    infoLineContainer: {
        display: "flex"
    }
}));

export interface IComment {
    id: number
    shipmentId: number
    type: string
    creator: string
    text: string
    permissions: string
    readablePermissions: string
    customizedPermissions: string[]
    creation: string
}

const CommentMessageDetails: React.FC<{ comment: IComment, expanded: boolean }> = ({comment, expanded}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    return (
        <Collapse in={expanded} unmountOnExit>
            <CardContent>
                <div className={classes.infoLineContainer}>
                    <Typography variant="body2" color="textSecondary"
                                className={classes.caption}>{t("comment.message-type")}:</Typography>
                    <Typography variant="body2" color="textSecondary">{comment.type}</Typography>
                </div>

                <div className={classes.infoLineContainer}>
                    <Typography variant="body2" color="textSecondary"
                                className={classes.caption}>{t("comment.permissions")}:</Typography>
                    <Typography variant="body2" color="textSecondary">{comment.readablePermissions}</Typography>
                </div>
            </CardContent>
        </Collapse>
    );
};

const CommentCard: React.FC<{ comment: IComment, withCommentDetails?: boolean, shipmentId: string | number }> = ({comment, withCommentDetails, shipmentId}) => {

    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const {uuid} = useParams();

    const date = comment.creation
        .replace("T", " ")
        .replace(/\.[\d+]*/, "");

    const [expanded, setExpanded] = React.useState<boolean>(false);
    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const onCommentClick = () => {
        navigate({
            pathname: `${COMMUNICATOR_PAGE_PATH}/${uuid}/${shipmentId}`,
            search: `redirect=${encodeURIComponent(location.pathname + location.search)}`
        })
    };

    return (
        <Card className={classes.root}>
            <CardActionArea className={classes.content} onClick={() =>
                withCommentDetails ? handleExpandClick() : onCommentClick()
            }>

                <div className={classes.details}>
                    <CardHeader
                        avatar={<Avatar>{comment.creator[0]}</Avatar>}
                        title={comment.creator}
                        subheader={date}
                    />

                    <CommentMessageDetails comment={comment} expanded={expanded}/>

                    <CardContent>
                        <Typography variant="body2" component="p">
                            {comment.text}
                        </Typography>
                    </CardContent>
                </div>

                <CardActions>
                    {withCommentDetails ?
                        <IconButton className={expanded ? classes.expandIconOpen : undefined}
                                    onClick={handleExpandClick} component="div">
                            <ExpandMoreIcon/>
                        </IconButton>
                        : <IconButton size="medium" color="primary" component="div">
                            <ArrowForwardIos/>
                        </IconButton>
                    }
                </CardActions>

            </CardActionArea>
        </Card>
    )
};

const CommentsList: React.FC<{ shipmentId: string | number | undefined, withCommentDetails?: boolean }> = ({shipmentId, withCommentDetails}) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [comments, setComments] = React.useState<IComment[] | undefined>(undefined);

    React.useEffect(() => {
        if (shipmentId) {
            getAllComments(shipmentId).then(({data}) => {
                const {status, response} = data;

                if (status !== RR_STATUS.OK) {
                    enqueueSnackbar(t("core.server-error"), {
                        variant: "error",
                        preventDuplicate: false
                    });
                } else {
                    setComments(response);
                }
            })
        }
    }, [shipmentId, enqueueSnackbar, t]);

    if (!comments || !shipmentId) {
        return <Loader/>;
    }

    if (comments.length < 1) {
        return <Typography>{t("comment.no-comments-found")}</Typography>
    }

    return <List>
        {
            comments.map((comment) => {
                return (
                    <ListItem key={comment.id} disableGutters>
                        <CommentCard comment={comment} shipmentId={shipmentId} withCommentDetails={withCommentDetails}/>
                    </ListItem>
                );
            })
        }
    </List>
};

export default CommentsList;